import { useContext, useEffect, useMemo, useRef } from 'react';
import type { FC, MouseEvent } from 'react';
import styled, { css } from 'styled-components';

import {
  Avatar,
  Button,
  calculateFontSize,
  DateTimeDisplay,
  defaultThemeProp,
  Icon,
  isValidDate,
  MenuButton,
  MetaList,
  NoValue,
  registerIcon,
  Status,
  StyledSecondary,
  SummaryItem,
  Text,
  Tooltip,
  useConfiguration,
  useElement,
  useI18n,
  useModalManager
} from '@pega/cosmos-react-core';
import type { FontSize, MetaListProps, ModalMethods } from '@pega/cosmos-react-core';
import { parseToDate } from '@pega/cosmos-react-core/lib/components/DateTime/Input/utils';
import { formatDateTime } from '@pega/cosmos-react-core/lib/components/DateTime/utils';
import { formatListToLocaleString } from '@pega/cosmos-react-core/lib/utils';
import * as usersSolidIcon from '@pega/cosmos-react-core/lib/components/Icon/icons/users-solid.icon';

import AutopilotAvatar from '../Autopilot/AutopilotAvatar';

import { FeedContext } from './Feed.context';
import FeedModalList from './FeedModalList';
import FeedButton from './FeedButton';
import type { FeedContentHeaderProps, FeedModalListProps } from './Feed.types';

registerIcon(usersSolidIcon);

const StyledFeedContentHeader = styled.div(({ theme }) => {
  return css`
    margin-block-end: ${theme.base.spacing};
    column-gap: ${theme.base.spacing};

    ${StyledSecondary} {
      ${FeedButton} {
        /* Negative margin needed to align the icon with the author name on the line above. */
        margin-inline-start: calc(-0.75 * ${theme.base.spacing});
      }
    }
  `;
});

StyledFeedContentHeader.defaultProps = defaultThemeProp;

const StyledName = styled(Text)`
  display: inline-block;
`;

const StyledIcon = styled(Icon)(({ theme }) => {
  const fontSizes = calculateFontSize(theme.base['font-size'], theme.base['font-scale']);

  return css`
    height: ${fontSizes[theme.components.text.secondary['font-size'] as FontSize]};
    fill: ${theme.base.palette['foreground-color']};
    padding-inline-end: calc(0.5 * ${theme.base.spacing});
  `;
});

StyledIcon.defaultProps = defaultThemeProp;

const FeedContentHeader: FC<FeedContentHeaderProps> = (props: FeedContentHeaderProps) => {
  const {
    variant,
    id,
    fullname,
    username,
    postTimestamp,
    avatarSrc,
    icon,
    userStatus,
    visibility,
    postType,
    customPostType,
    postContext,
    recipients = [],
    recipientsCount = 0,
    menuItems,
    edited,
    editTimestamp,
    recipientsLoading,
    onEdit,
    onLoadMoreRecipients,
    onRecipientInteraction,
    onUserClick
  } = props;

  const {
    autopilotInfo: { id: autopilotId, label: autopilotLabel },
    userInfo: loggedInUser
  } = useContext(FeedContext);
  const t = useI18n();
  const { locale } = useConfiguration();
  const { create } = useModalManager();

  const recipientsModalRef = useRef<ModalMethods<FeedModalListProps>>();
  const [postTimestampEl, setPostTimestampEl] = useElement<HTMLSpanElement>();
  const [editTimestampEl, setEditTimestampEl] = useElement<HTMLSpanElement>();

  const autopilotAuthor = username === autopilotId;

  const metaData: MetaListProps['items'] = [];

  if (postType && customPostType) {
    metaData.push(<Status variant='warn'>{postType.label}</Status>);
  }

  metaData.push(
    <>
      <DateTimeDisplay ref={setPostTimestampEl} value={postTimestamp} variant='relative' />
      {postTimestamp && (
        <Tooltip target={postTimestampEl}>
          {formatDateTime(parseToDate(postTimestamp), {
            t,
            locale,
            format: 'medium',
            variant: 'datetime',
            timeZone: loggedInUser.timeZone
          })}
        </Tooltip>
      )}
    </>
  );

  if (edited) {
    metaData.push(
      <>
        <Text ref={setEditTimestampEl} variant='secondary'>
          {t('edited', [
            (editTimestamp || editTimestamp === 0) && isValidDate(parseToDate(editTimestamp)) ? (
              formatDateTime(parseToDate(editTimestamp), {
                t,
                locale,
                format: 'medium',
                variant: 'relative'
              })
            ) : (
              <NoValue />
            )
          ])}
        </Text>
        {editTimestamp && (
          <Tooltip target={editTimestampEl}>
            {formatDateTime(parseToDate(editTimestamp), {
              t,
              locale,
              format: 'medium',
              variant: 'datetime',
              timeZone: loggedInUser.timeZone
            })}
          </Tooltip>
        )}
      </>
    );
  }

  if (postType && !customPostType) {
    if (postType.icon) {
      metaData.push(
        <>
          <StyledIcon name={postType.icon} />
          <Text variant='secondary'>{postType.label}</Text>
        </>
      );
    } else {
      metaData.push(postType.label);
    }
  }

  if (visibility === 'private') {
    const recipientsList = recipients.map(x => x.primary);

    if (recipientsList.length === 0) {
      metaData.push(`${t('feed_visible_to')} ${t('you').toLowerCase()}`);
    } else if (recipientsList.length === 1) {
      metaData.push(`${t('feed_visible_to')} ${recipients[0].primary}`);
    } else {
      metaData.push(
        <>
          <Text variant='secondary'>{t('feed_visible_to')} </Text>
          <Button
            variant='link'
            onClick={() => {
              recipientsModalRef.current = create(FeedModalList, {
                heading: t('feed_visible_to'),
                listItems: recipients,
                onLoadMore: onLoadMoreRecipients,
                loading: recipientsLoading
              });
            }}
            onFocus={onRecipientInteraction}
            onMouseOver={onRecipientInteraction}
            aria-label={`${t('feed_private')} ${t('feed_visible_to')} ${formatListToLocaleString(
              recipientsList,
              t,
              locale,
              {
                count: recipientsCount
              }
            )}`}
            label={formatListToLocaleString(recipientsList, t, locale, {
              count: recipientsCount,
              separator: '\n'
            })}
          >
            {recipientsCount} {t('feed_users').toLowerCase()}
          </Button>
        </>
      );
    }
  }

  const userVisual = autopilotAuthor ? (
    <AutopilotAvatar />
  ) : (
    <Avatar
      name={fullname}
      size={variant === 'post' ? 'l' : 'm'}
      imageSrc={avatarSrc}
      icon={icon}
      status={userStatus}
    />
  );

  const actionMenuItems = useMemo(() => {
    let actions: FeedContentHeaderProps['menuItems'] = [];

    if (username === loggedInUser.username && onEdit) {
      actions.push({
        primary: t('edit'),
        id: 'edit',
        onClick: () => onEdit({ id, postType, visibility, recipients })
      });
    }

    if (menuItems) {
      actions = actions.concat(menuItems);
    }

    return actions;
  }, [menuItems, username, loggedInUser.username, onEdit, id, postType, visibility, recipients]);

  useEffect(() => {
    if (recipients.length) {
      recipientsModalRef.current?.update({
        heading: t('feed_visible_to'),
        listItems: recipients,
        onLoadMore: onLoadMoreRecipients,
        loading: recipientsLoading
      });
    }
  }, [recipients, onLoadMoreRecipients, recipientsLoading]);

  return (
    <SummaryItem
      as={StyledFeedContentHeader}
      visual={userVisual}
      primary={
        <div>
          {onUserClick ? (
            <Button
              variant='text'
              onClick={(event: MouseEvent) => {
                onUserClick?.({ id, username }, event);
              }}
            >
              <Text variant={variant === 'post' ? 'h3' : 'h4'}>
                {autopilotAuthor ? autopilotLabel : fullname}
              </Text>
            </Button>
          ) : (
            <StyledName variant={variant === 'post' ? 'h3' : 'h4'}>
              {autopilotAuthor ? autopilotLabel : fullname}
            </StyledName>
          )}
          {postContext && <Text variant='primary'> {t('feed_context', [postContext])}</Text>}
        </div>
      }
      secondary={<MetaList items={metaData} />}
      actions={
        actionMenuItems.length ? (
          <MenuButton
            text={t('actions')}
            aria-label={t('actions_for', [fullname])}
            menu={{
              items: actionMenuItems
            }}
            icon='more'
            iconOnly
            variant='simple'
          />
        ) : undefined
      }
    />
  );
};

export default FeedContentHeader;
